import React from "react";
import { Ban, Check, CircleHelp, Loader2 } from "lucide-react";

import type { ConnectorTool, ToolPermission } from "@/hooks/useConnectors";

// Three states, not a switch, because "ask me" is the useful middle and a boolean cannot express it.
// A write tool posts to a channel or sends mail as the user, unattended, inside a Temporal workflow —
// so the honest default is a question, and the control has to make that a real option.

const OPTIONS: { value: ToolPermission; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { value: "always", label: "Always allow", Icon: Check },
  { value: "ask", label: "Ask each time", Icon: CircleHelp },
  { value: "never", label: "Never allow", Icon: Ban },
];

interface ToolPermissionsProps {
  tools: ConnectorTool[];
  busy?: boolean;
  disabled?: boolean;
  onChange: (toolName: string, permission: ToolPermission) => void;
  onChangeAll: (permission: ToolPermission) => void;
}

const ToolPermissions: React.FC<ToolPermissionsProps> = ({
  tools,
  busy,
  disabled,
  onChange,
  onChangeAll,
}) => {
  if (tools.length === 0) return null;

  const selector = (
    current: ToolPermission,
    label: string,
    onPick: (p: ToolPermission) => void,
  ) => (
    <div className="flex shrink-0 gap-1" role="group" aria-label={`${label} permission`}>
      {OPTIONS.map(({ value, label: optionLabel, Icon }) => {
        const active = current === value;
        return (
          <button
            key={value}
            type="button"
            disabled={disabled || busy}
            aria-pressed={active}
            aria-label={`${optionLabel} — ${label}`}
            title={optionLabel}
            onClick={() => onPick(value)}
            className={`flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50 ${
              active
                ? "bg-ink-blue-900 text-white dark:bg-white/10"
                : "border border-gray-200 text-muted-foreground hover:border-gray-300 hover:text-foreground dark:border-white/10 dark:text-gray-500 dark:hover:border-white/20 dark:hover:text-gray-300"
            }`}
          >
            <Icon className="h-3 w-3" />
          </button>
        );
      })}
    </div>
  );

  // Only reflects a shared value when every tool agrees; otherwise it would misreport what is set
  const shared = tools.every((t) => t.permission === tools[0].permission) ? tools[0].permission : null;

  return (
    <section className="rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
      <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-3.5 py-3 dark:border-white/10">
        <div className="min-w-0">
          <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            Manage my agent's permissions
            {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
          </h4>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Reads run without asking. Anything that writes checks with you first.
          </p>
        </div>
      </header>

      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/60 px-3.5 py-2.5 dark:border-white/10 dark:bg-white/5">
        <div className="min-w-0">
          <p className="text-xs font-medium text-foreground">Manage all permissions</p>
          <p className="text-[11px] text-muted-foreground">Set every tool at once</p>
        </div>
        {selector(shared ?? "ask", "all tools", onChangeAll)}
      </div>

      <ul className="divide-y divide-gray-100 dark:divide-white/10">
        {tools.map((tool) => (
          <li key={tool.name} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-foreground">
                {tool.title}
                {tool.kind === "write" && (
                  <span className="rounded bg-amber-50 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
                    Writes
                  </span>
                )}
              </p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">{tool.description}</p>
            </div>
            {selector(tool.permission, tool.title, (p) => onChange(tool.name, p))}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default ToolPermissions;
